import { SvgIcon, Message, IconFont, BasSwitch } from '@/components'
import {
  Button,
  Form,
  Input,
  Radio,
  Select,
  Tooltip,
  InputNumber,
  Checkbox,
  DatePicker,
  Cascader,
  Typography,
  Table,
  Divider,
  Space,
} from 'antd'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { usePostCreateTask, useTaskGroup } from '../pre-create-task/utils'
import {
  useAgentList,
  useCaseTemplateIdsAll,
  useCaseTemplateList,
  useDeviceListByHostId,
  useGetTaskBaseInfo,
  useHostList,
  useUsCaseIdsAll,
  useUsCaseList,
  useUserEmailList,
  useAttackTypeList,
} from './utils'
import { useGetUseCaseTagList } from '@/pages/useCase-knowledge-base/useCase-center/util'
import { GetCaseTemplateList, GetUsCaseList } from '@/api/endpoint-security/create'
import { formatDateTime } from '@/utils/date'
import Scrollbars from 'react-custom-scrollbars'
import { TablePaginationConfig } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import { debounce } from 'lodash'
import lowImg from '@/assets/imges/leak-low.png'
import centerkImg from '@/assets/imges/leak-center.png'
import highImg from '@/assets/imges/leak-high.png'
import severityImg from '@/assets/imges/leak-severity.png'
import AddTemplateModal from './add-template-modal'
import DetailDrawer from './detail-drawer'
import { Configure } from './configure'
import moment from 'moment'
import { useGetAuthorizeConfig } from '@/pages/account-center/authorize-manage/utils'
import { disabledDate, disabledDateTime } from '@/utils/appointment'
import { t } from '@/i18n'

const { Text } = Typography

// IP地址正则表达式
const ipPattern =
  /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/

interface TemplateList {
  templateId: string
  name: string
  isUpdate: boolean
  originType: string
  resourceAmount: number
  resourceSelectedAmount: number
  updateTime: string
  usCaseIds: string[]
}

interface UsCaseList {
  templateIds: string[]
  resourceId: string
  name: string
  riskLevel: string
  resourceLevel: string
  attackTypeId: string
  originType: string
  updateTime: string
}

// 自定义Hook：处理模板数据获取
const useTemplateDataFetch = (
  getCaseTemplateList: (params: GetCaseTemplateList) => Promise<any>,
  getCaseTemplateIdsAll: (params: GetCaseTemplateList) => Promise<any>,
) => {
  const [isLoading, setIsLoading] = useState(false)

  const fetchTemplateData = useCallback(
    async (
      searchParams: GetCaseTemplateList,
      processData: (data: any[], isLatest: boolean) => any[],
      form: any,
      setTemplateList: (list: any[]) => void,
      setTotal: (total: number) => void,
    ) => {
      setIsLoading(true)

      try {
        const [templateRes, templateIdsRes] = await Promise.allSettled([
          getCaseTemplateList(searchParams),
          getCaseTemplateIdsAll({
            ...searchParams,
            page: undefined,
            pageSize: undefined,
            os: undefined,
          }),
        ])

        if (templateRes.status === 'fulfilled') {
          const isLatest = form.getFieldValue('isLatest')
          const newTemplateList = processData(templateRes.value.data.list, isLatest)

          setTemplateList(newTemplateList)
          setTotal(templateRes.value.data.total)
        }

        // 记录失败的API调用
        if (templateRes.status === 'rejected') {
          console.error('Failed to fetch template list:', templateRes.reason)
        }
        if (templateIdsRes.status === 'rejected') {
          console.error('Failed to fetch template IDs:', templateIdsRes.reason)
        }
      } catch (error) {
        console.error('Template data fetch failed:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getCaseTemplateList, getCaseTemplateIdsAll],
  )

  return { fetchTemplateData, isLoading }
}

// 自定义Hook：处理用例数据获取
const useUsCaseDataFetch = (
  getUsCaseList: (params: GetUsCaseList & { isSearch?: boolean }) => Promise<any>,
  getUsCaseIdsAll: (params: GetUsCaseList & { isSearch?: boolean }) => Promise<any>,
  getAttackTypeList: (params: {
    queryType: string
    'createTaskResourceListQuery.templateIds': string[]
  }) => Promise<any>,
) => {
  const [isLoading, setIsLoading] = useState(false)

  const fetchUsCaseData = useCallback(
    async (
      searchParams: GetUsCaseList & { isSearch?: boolean },
      setUsCaseList: (list: UsCaseList[]) => void,
      setUsCaseTotal: (total: number) => void,
    ) => {
      setIsLoading(true)

      try {
        // 并行执行API调用以提高性能
        const [usCaseRes, usCaseIdsRes] = await Promise.allSettled([
          getUsCaseList({ ...searchParams, isSearch: undefined }),
          getUsCaseIdsAll({
            ...searchParams,
            page: undefined,
            pageSize: undefined,
            isSearch: undefined,
          }),
        ])

        if (usCaseRes.status === 'fulfilled' && usCaseIdsRes.status === 'fulfilled') {
          // 获取攻击类型数据
          await getAttackTypeList({
            queryType: 'query_type_create_task_resource_list',
            'createTaskResourceListQuery.templateIds': searchParams.templateId,
          })

          // 处理用例数据
          const newUsCaseList = usCaseRes.value.data.list.map(item => ({
            templateIds: item.templateIds,
            resourceId: item.resourceId,
            name: item.name,
            riskLevel: item.riskLevel,
            resourceLevel: item.resourceLevel,
            attackTypeId: item.attackTypeId,
            originType: item.originType,
            updateTime: item.updateTime,
          }))

          setUsCaseList(newUsCaseList)
          setUsCaseTotal(usCaseRes.value.data.total)
        }

        // 记录失败的API调用
        if (usCaseRes.status === 'rejected') {
          console.error('Failed to fetch use case list:', usCaseRes.reason)
        }
        if (usCaseIdsRes.status === 'rejected') {
          console.error('Failed to fetch use case IDs:', usCaseIdsRes.reason)
        }
      } catch (error) {
        console.error('Use case data fetch failed:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getUsCaseList, getUsCaseIdsAll, getAttackTypeList],
  )

  return { fetchUsCaseData, isLoading }
}

const CreateTask: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = (location.state as { id?: string }) || {}
  const [step, setStep] = useState(1)
  const [taskType, setTaskType] = useState('scheduler_type_immediate')
  const [verificationType, setVerificationType] = useState('VerificationTypeOnline')
  const [executeMode, setExecuteMode] = useState<string[]>(['agentToHost'])
  const [nat, setNat] = useState(false)
  const [send, setSend] = useState(false)
  const [filter, setFilter] = useState(false)
  const [total, setTotal] = useState(0)
  const [templateList, setTemplateList] = useState<TemplateList[]>([])
  const [templateSelectionMap, setTemplateSelectionMap] = useState<
    Record<string, { resourceSelectedAmount: number; usCaseIds: string[] }>
  >({})
  const [executeUpdateIds, setExecuteUpdateIds] = useState<string[]>([])
  const [usCaseTotal, setUsCaseTotal] = useState(0)
  const [usCaseList, setUsCaseList] = useState<UsCaseList[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedUsCaseRowKeys, setSelectedUsCaseRowKeys] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [searchType, setSearchType] = useState('name')
  const [usCaseSearchValue, setUsCaseSearchValue] = useState('')
  const [cascaderValue, setCascaderValue] = useState<(string | number)[]>([])
  const [templateSearchParams, setTemplateSearchParams] = useState<GetCaseTemplateList>({
    page: 1,
    pageSize: 10,
  })
  const [usCaseSearchParams, setUsCaseSearchParams] = useState<
    GetUsCaseList & { isSearch?: boolean }
  >({
    page: 1,
    pageSize: 10,
    templateId: [],
    queryType: 'query_bind',
  })
  const [typeSelectOpen, setTypeSelectOpen] = useState(false)
  const [riskLevelSelectOpen, setRiskLevelSelectOpen] = useState(false)
  const [resourceLevelSelectOpen, setResourceLevelSelectOpen] = useState(false)
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)
  const [detailDrawer, setDetailDrawer] = useState<{ visible: boolean; id: string }>({
    visible: false,
    id: '',
  })
  const [addTemplateModal, setAddTemplateModal] = useState<{ visible: boolean; ids: string[] }>({
    visible: false,
    ids: [],
  })
  const [configureVisible, setConfigureVisible] = useState(false)
  const [createParams, setCreateParams] = useState({})
  const [listenerHostIp, setListenerHostIp] = useState('')

  // 获取授权配置
  const [authConfig, getAuthorizeConfig] = useGetAuthorizeConfig()
  // 获取任务分组
  const [taskGroup, getTaskGroup] = useTaskGroup()
  // 获取agent列表
  const [getAgentList, agentList] = useAgentList()
  // 获取主机列表
  const [getHostList, hostList] = useHostList()
  // 根据hostId获取设备列表
  const [getDeviceList, deviceList] = useDeviceListByHostId()
  // 获取用例模板列表
  const [getCaseTemplateList, getCaseTemplateListLoading] = useCaseTemplateList()
  // 获取用例模板列表筛选的全量模板ids
  const [getCaseTemplateIdsAll, caseTemplateIdsAll] = useCaseTemplateIdsAll()
  // 获取模板关联/未关联用例列表
  const [getUsCaseList, getUsCaseListLoading] = useUsCaseList()
  // 根据筛选条件获取模板关联/未关联用例ids
  const [getUsCaseIdsAll, usCaseIdsAll] = useUsCaseIdsAll()
  // 获取用例标签列表
  const [getUseCaseTagList, useCaseTagList] = useGetUseCaseTagList()
  // 获取攻击类型列表
  const [getAttackTypeList, attackTypeList] = useAttackTypeList()
  // 获取人员邮箱列表
  const [getUserEmailList, userEmailList] = useUserEmailList()
  // 创建任务
  const [createTask, createTaskLoading] = usePostCreateTask()
  // 获取任务基础信息（任务复制）
  const [getTaskBaseInfo] = useGetTaskBaseInfo()

  // 使用自定义Hook处理模板数据获取
  const { fetchTemplateData, isLoading: isTemplateLoading } = useTemplateDataFetch(
    getCaseTemplateList,
    getCaseTemplateIdsAll,
  )

  // 使用自定义Hook处理用例数据获取
  const { fetchUsCaseData, isLoading: isUsCaseLoading } = useUsCaseDataFetch(
    getUsCaseList,
    getUsCaseIdsAll,
    getAttackTypeList,
  )

  const verificationTypeOptions = useMemo(() => {
    if (authConfig?.offlineVerification === 'On') {
      return [
        {
          label: t('在线验证'),
          value: 'VerificationTypeOnline',
        },
        {
          label: t('离线验证'),
          value: 'VerificationTypeOffline',
        },
      ]
    } else {
      return [
        {
          label: t('在线验证'),
          value: 'VerificationTypeOnline',
        },
      ]
    }
  }, [authConfig])

  const agentListOptions = useMemo(() => {
    switch (verificationType) {
      case 'VerificationTypeOnline':
        return agentList.map(domain => ({
          value: domain.id,
          label: domain.name,
          children: domain.os.map(osType => ({
            value: osType.osSystem,
            label: osType.osSystem === 'OsWin' ? 'Windows' : 'Linux',
            children: osType.agents.map(agent => ({
              value: agent.agentId,
              label: (
                <div className="agent-item">
                  <div className="agent-item-name">
                    <Tooltip title={agent.ip.join(',')} overlayStyle={{ maxWidth: '400px' }}>
                      {agent.agentName}
                      <span style={{ color: '#999', marginLeft: 4 }}>({agent.ip[0]})</span>
                    </Tooltip>
                  </div>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      background: agent.aliveStatus === 'AliveStatusTrue' ? '#67C23A' : '#677280',
                      borderRadius: '50%',
                    }}
                  />
                </div>
              ),

              selectedLabel: (
                <div>
                  <Tooltip title={agent.ip.join(',')} overlayStyle={{ maxWidth: '400px' }}>
                    {agent.agentName}
                    <span style={{ color: '#999', marginLeft: 4 }}>({agent.ip[0]})</span>
                  </Tooltip>
                </div>
              ),

              disabled: agent.aliveStatus === 'AliveStatusFalse',
            })),
          })),
        }))

      case 'VerificationTypeOffline':
        return [
          {
            value: 'supported_architecture_x86',
            label: 'X86',
            children: [
              {
                value: 'supported_platform_linux',
                label: 'Linux',
              },
              {
                value: 'supported_platform_windows',
                label: 'Windows',
              },
            ],
          },
          {
            value: 'supported_architecture_arm',
            label: 'ARM',
            children: [
              {
                value: 'supported_platform_linux',
                label: 'Linux',
              },
            ],
          },
        ]

      default:
        return []
    }
  }, [agentList, verificationType])

  const hostListOptions = useMemo(
    () =>
      hostList.map(domain => ({
        value: domain.id,
        label: domain.name,
        children: domain.os.map(osType => ({
          value: osType.osSystem,
          label: osType.osSystem === 'OsWin' ? 'Windows' : 'Linux',
          children: osType.hosts.map(host => ({
            value: host.hostId,
            label: (
              <div className="agent-item">
                <div className="agent-item-name">
                  <Tooltip
                    title={
                      !host.hasDefaultDevice ? t('无安全设备，请对该主机关联默认验证设备') : ''
                    }
                  >
                    <SvgIcon
                      name={host.hasDefaultDevice ? '设备' : '设备灰'}
                      width="12px"
                      height="12px"
                      style={{ marginRight: 4 }}
                    />
                  </Tooltip>
                  <Tooltip title={host.ip.join(',')} overlayStyle={{ maxWidth: '400px' }}>
                    {host.hostName}
                    <span style={{ color: '#999', marginLeft: 4 }}>({host.ip[0]})</span>
                  </Tooltip>
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    background: host.aliveStatus === 'AliveStatusTrue' ? '#67C23A' : '#677280',
                    borderRadius: '50%',
                  }}
                />
              </div>
            ),

            selectedLabel: (
              <div>
                <Tooltip title={host.ip.join(',')} overlayStyle={{ maxWidth: '400px' }}>
                  {host.hostName}
                  <span style={{ color: '#999', marginLeft: 4 }}>({host.ip[0]})</span>
                </Tooltip>
              </div>
            ),

            disabled: host.aliveStatus !== 'AliveStatusTrue',
          })),
        })),
      })),
    [hostList],
  )

  const listenerListOptions = useMemo(
    () =>
      hostList.map(domain => ({
        value: domain.id,
        label: domain.name,
        children: domain.os.map(osType => ({
          value: osType.osSystem,
          label: osType.osSystem === 'OsWin' ? 'Windows' : 'Linux',
          children: osType.hosts.map(host => ({
            value: host.hostId,
            label: (
              <div className="agent-item">
                <div className="agent-item-name">{host.hostName}</div>
                <span
                  style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    background: host.aliveStatus === 'AliveStatusTrue' ? '#67C23A' : '#677280',
                    borderRadius: '50%',
                  }}
                />
              </div>
            ),

            selectedLabel: host.hostName,
            disabled: host.aliveStatus === 'AliveStatusFalse',
            children: host.ip.map(ip => ({
              value: ip,
              label: ip,
            })),
          })),
        })),
      })),
    [hostList],
  )

  const deviceListOptions = useMemo(
    () =>
      deviceList.map(domain => ({
        value: domain.deviceType,
        label: domain.deviceType,
        children: domain.devices.map(device => ({
          value: device.deviceId,
          label: (
            <div className="agent-item">
              <div className="agent-item-name">
                {device.deviceName} ｜
                <span style={{ color: '#999', marginLeft: 4 }}>({device.deviceIp})</span>
              </div>
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  background:
                    device.deviceStatus === 'DatasourceClientStatusOnline' ? '#67C23A' : '#677280',
                  borderRadius: '50%',
                }}
              />
            </div>
          ),

          selectedLabel: (
            <div>
              {device.deviceName}｜
              <span style={{ color: '#999', marginLeft: 4 }}>({device.deviceIp})</span>
            </div>
          ),

          disabled: device.deviceStatus === 'DatasourceClientStatusOffline',
        })),
      })),
    [deviceList],
  )

  const agentDisplayRender = (labels: string[], selectedOptions?: any[]) => {
    if (!selectedOptions || selectedOptions.length < 3) return labels.join(' / ')

    const agent = selectedOptions[2]
    return agent?.selectedLabel
  }

  const hostDisplayRender = (labels: string[], selectedOptions?: any[]) => {
    if (!selectedOptions || selectedOptions.length < 3) return labels.join(' / ')

    const host = selectedOptions[2]
    return host.selectedLabel
  }

  const listenerDisplayRender = (labels: string[], selectedOptions?: any[]) => {
    if (!selectedOptions || selectedOptions.length < 3) return labels.join(' / ')

    const host = selectedOptions[2]
    const hostIp = selectedOptions[3]?.value
    return `${host.selectedLabel} / ${hostIp || ''}`
  }

  const deviceDisplayRender = (labels: string[], selectedOptions?: any[]) => {
    if (!selectedOptions || selectedOptions.length < 2) return labels.join(' / ')

    const device = selectedOptions[1]
    return device.selectedLabel
  }

  // 用例标签级联选择器数据
  const cascaderOptions = useMemo(
    () => ({
      tagId: useCaseTagList.map(item => ({
        value: item.tagCategoryId,
        label: (
          <span>
            <span>{item.tagCategoryName}</span>
            <span style={{ color: 'rgb(32, 144, 168)' }}>({item.tagCategoryNum})</span>
          </span>
        ),

        children: item.tagList.map(tag => ({
          value: tag.tagId,
          label: (
            <span>
              <span>{tag.tagName}</span>
              <span style={{ color: 'rgb(32, 144, 168)' }}>({tag.tagNum})</span>
            </span>
          ),
        })),
      })),
    }),
    [useCaseTagList],
  )

  // 计算是否全选
  const isAllSelected = useMemo(
    () => usCaseTotal > 0 && selectedUsCaseRowKeys.length === usCaseTotal,
    [usCaseTotal, selectedUsCaseRowKeys],
  )

  // 模板列表表格列配置
  const columns = [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      width: 120,
      fixed: 'left' as const,
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ maxWidth: 120 }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: t('模板类型'),
      dataIndex: 'originType',
      key: 'originType',
      width: 90,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={typeSelectOpen}
            onDropdownVisibleChange={setTypeSelectOpen}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                      setTypeSelectOpen(false)
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { value: 'OriginTypeSystem', label: t('系统内置') },
              { value: 'OriginTypeCustom', label: t('手动添加') },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      onFilter: (value, record) =>
        record.originType && Array.isArray(value)
          ? value.includes(record.originType)
          : record.originType === value,
      render: (text: string) => (
        <TypeTag bgColor={text === 'OriginTypeSystem' ? '#1F8FA7' : '#F77B83'} color={'#fff'}>
          {text === 'OriginTypeSystem' ? t('系统内置') : t('手动添加')}
        </TypeTag>
      ),
    },
    {
      title: t('执行更新'),
      width: 70,
      dataIndex: 'isUpdate',
      key: 'isUpdate',
      render: (text: boolean, record: TemplateList) => (
        <BasSwitch
          disabled={
            !(form.getFieldValue('isLatest') && selectedRowKeys.includes(record.templateId)) ||
            record.resourceAmount === 0 ||
            record.resourceSelectedAmount !== record.resourceAmount
          }
          checked={text}
          onChange={e => handleIsUpdateChange(e, record.templateId)}
        />
      ),
    },
    {
      title: t('全部|已选用例数'),
      dataIndex: 'resourceAmount',
      key: 'resourceAmount',
      width: 120,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (_, record: TemplateList) => (
        <span>
          {record.resourceAmount} | {record.resourceSelectedAmount || 0}
        </span>
      ),
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 100,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
  ]

  // 用例列表表格列配置
  const usCaseColumns = [
    {
      title: t('用例名称'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      width: 140,
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
          style={{ maxWidth: 140 }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: t('危险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 90,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={riskLevelSelectOpen}
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            getPopupContainer={() => document.body}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { value: 'RiskLevelGross', label: t('严重') },
              { value: 'RiskLevelHigh', label: t('高危') },
              { value: 'RiskLevelMiddle', label: t('中危') },
              { value: 'RiskLevelLow', label: t('低危') },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      onFilter: (value, record) =>
        record.riskLevel && Array.isArray(value)
          ? value.includes(record.riskLevel)
          : record.riskLevel === value,
      render: (level: string) => {
        const levelMap = {
          RiskLevelGross: t('严重'),
          RiskLevelHigh: t('高危'),
          RiskLevelMiddle: t('中危'),
          RiskLevelLow: t('低危'),
        }
        const config = {
          严重: { color: '#B70000', bg: '#F5D9D9' },
          高危: { color: '#FF4141', bg: '#FFE1E1' },
          中危: { color: '#FFA709', bg: '#FFEDCE' },
          低危: { color: '#6F9FFF', bg: '#E1EBFE' },
        }
        const displayLevel = levelMap[level] || t('低危')
        const style = config[displayLevel]
        return (
          <span>
            <img
              src={
                level === 'RiskLevelLow'
                  ? lowImg
                  : level === 'RiskLevelMiddle'
                  ? centerkImg
                  : level === 'RiskLevelHigh'
                  ? highImg
                  : severityImg
              }
              alt=""
              style={{ width: '12px', height: '12px', marginRight: '4px' }}
            />

            <TypeTag bgColor={style.bg} color={style.color}>
              {displayLevel}
            </TypeTag>
          </span>
        )
      },
    },
    {
      title: t('用例类型'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: 90,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { value: 'ResourceLevelSenior', label: t('高级') },
              { value: 'ResourceLevelMiddle', label: t('中级') },
              { value: 'ResourceLevelJunior', label: t('初级') },
            ]}
            placeholder={t('请选择')}
            allowClear
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      onFilter: (value, record) =>
        record.resourceLevel && Array.isArray(value)
          ? value.includes(record.resourceLevel)
          : record.resourceLevel === value,
      render: level => {
        const levelMap = {
          ResourceLevelSenior: t('高级'),
          ResourceLevelMiddle: t('中级'),
          ResourceLevelJunior: t('初级'),
        }
        const config = {
          高级: { color: '#fff', bg: '#F44F4F' },
          中级: { color: '#fff', bg: '#FFA709' },
          初级: { color: '#fff', bg: '#6F9FFF' },
        }
        const displayLevel = levelMap[level] || t('初级')
        const style = config[displayLevel]
        return (
          <TypeTag bgColor={style.bg} color={style.color}>
            {displayLevel}
          </TypeTag>
        )
      },
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackTypeId',
      key: 'attackTypeId',
      width: 100,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            value={selectedKeys as string[]}
            open={attackTypeSelectOpen}
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            getPopupContainer={() => document.body}
            onChange={value => {
              setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      clearFilters?.()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={attackTypeList.map(item => ({
              value: item.id,
              label: item.name,
            }))}
            placeholder={t('请选择')}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      onFilter: (value, record) =>
        record.attackTypeId && Array.isArray(value)
          ? value.includes(record.attackTypeId)
          : record.attackTypeId === value,
      render: (text: string) => {
        const attackType = attackTypeList.find(item => item.id === text)
        return attackType?.name || '-'
      },
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 100,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: 50,
      fixed: 'right' as const,
      render: (_, record: UsCaseList) => (
        <Button
          type="link"
          onClick={() => {
            setDetailDrawer({ visible: true, id: record.resourceId })
          }}
          style={{ padding: '6.6px 0' }}
        >
          {t('详情')}
        </Button>
      ),
    },
  ]

  // 下一步
  const handleNext = async () => {
    const executeModes = form.getFieldValue('executeMode')
    let verificationModeIds: number[] = []
    if (executeModes.includes('agentToHost')) {
      verificationModeIds.push(4)
    }
    if (executeModes.includes('agentToListener')) {
      verificationModeIds.push(9)
    }
    try {
      await form.validateFields()
      setStep(2)
      if (form.getFieldValue('agentToHost')?.agentId?.[1] !== templateSearchParams.os) {
        setTemplateList([])
        setTemplateSelectionMap({})
        setSelectedRowKeys([])
        setUsCaseList([])
        setSelectedUsCaseRowKeys([])
        setTotal(0)
        setUsCaseTotal(0)
      }

      setUsCaseSearchParams(prev => ({
        ...prev,
        page: 1,
        pageSize: 10,
        filterParams: {
          ...prev.filterParams,
          sceneId: [9],
          verificationModeId: verificationModeIds,
        },
        os:
          verificationType === 'VerificationTypeOnline'
            ? form.getFieldValue('agentToHost')?.agentId?.[1]
            : form.getFieldValue('agentToHost')?.agentId?.[1] === 'supported_platform_linux'
            ? 'OsLinux'
            : 'OsWin',
      }))
      setTemplateSearchParams(prev => ({
        ...prev,
        page: 1,
        pageSize: 10,
        filterParams: {
          ...prev.filterParams,
          sceneIds: [9],
          verificationModeIds,
        },
        os:
          verificationType === 'VerificationTypeOnline'
            ? form.getFieldValue('agentToHost')?.agentId?.[1]
            : form.getFieldValue('agentToHost')?.agentId?.[1] === 'supported_platform_linux'
            ? 'OsLinux'
            : 'OsWin',
      }))

      const isLatest = form.getFieldValue('isLatest')
      if (isLatest) {
        getCaseTemplateIdsAll({
          ...templateSearchParams,
          page: undefined,
          pageSize: undefined,
          os: undefined,
          filterParams: {
            ...templateSearchParams.filterParams,
            sceneIds: [9],
            verificationModeIds,
          },
        })
          .then(res => {
            setExecuteUpdateIds(res.data.templateIds)
          })
          .catch(_ => {})
      }
      let newTemplateList: any[] = []
      if (verificationModeIds === templateSearchParams.filterParams?.verificationModeIds) {
        newTemplateList = processTemplateData(templateList, isLatest)
      } else {
        if (templateList.length > 0) {
          setTemplateList([])
          setTemplateSelectionMap({})
          setTotal(0)
          setSelectedRowKeys([])
          setSelectedUsCaseRowKeys([])
          setUsCaseList([])
          setUsCaseTotal(0)
          setTemplateSearchParams(prev => ({
            ...prev,
            page: 1,
            pageSize: 10,
            filterParams: {
              ...prev.filterParams,
              sceneIds: [9],
              verificationModeIds,
            },
            os:
              verificationType === 'VerificationTypeOnline'
                ? form.getFieldValue('agentToHost')?.agentId?.[1]
                : form.getFieldValue('agentToHost')?.agentId?.[1] === 'supported_platform_linux'
                ? 'OsLinux'
                : 'OsWin',
          }))
        }
      }

      setTemplateList(newTemplateList)
    } catch (error) {
      Message.error(t('请填写完整信息'))
    }
  }

  // 上一步
  const handleBack = () => {
    setStep(1)
  }

  // 执行模式变化
  const handleExecuteModeChange = (value: CheckboxValueType[]) => {
    const prevMode = executeMode
    setExecuteMode(value as string[])

    // 如果全部取消勾选，清空所有相关表单
    if (value.length === 0) {
      form.setFieldsValue({
        agentToHost: undefined,
        agentToListener: undefined,
      })
      return
    }

    // 如果取消选中了某个模式，清空相关表单
    if (prevMode.includes('agentToHost') && !value.includes('agentToHost')) {
      // 判断 agentToListener 的 agentId 是否有值
      const listenerAgentId = form.getFieldValue(['agentToListener', 'agentId'])
      if (!listenerAgentId) {
        // 如果没有值，清空所有相关字段
        form.setFieldsValue({
          agentToHost: undefined,
        })
      } else {
        // 如果有值，只清空 hostId
        form.setFieldsValue({
          agentToHost: {
            hostId: undefined,
          },
        })
      }
    }

    if (prevMode.includes('agentToListener') && !value.includes('agentToListener')) {
      // 判断 agentToHost 的 agentId 是否有值
      const hostAgentId = form.getFieldValue(['agentToHost', 'agentId'])
      if (!hostAgentId) {
        // 如果没有值，清空所有相关字段
        form.setFieldsValue({
          agentToListener: undefined,
        })
      } else {
        // 如果有值，只清空 hostId dport dnatIp dnatPort
        form.setFieldsValue({
          agentToListener: {
            hostId: undefined,
            dport: undefined,
            dnatIp: undefined,
            dnatPort: undefined,
          },
        })
      }
    }
  }

  // Agent 变化
  const handleAgentChange = async (value: (string | number)[]) => {
    if (!value || value.length < 3) return

    // 为Agent → Listener 带出Listener Agent
    form.setFieldsValue({
      agentToListener: {
        agentId: value,
      },
      agentToHost: {
        agentId: value,
      },
    })

    const agentId = value[2]
    // 在 agentList 中查找选中的 agent
    const selectedAgent = agentList
      .flatMap(domain => domain.os)
      .flatMap(os => os.agents)
      .find(agent => agent.agentId === agentId)

    if (selectedAgent) {
      // 在 hostList 中查找对应的主机
      const targetHost = hostList
        .flatMap(domain => domain.os)
        .flatMap(os => os.hosts)
        .find(host => host.agentId === agentId)

      if (targetHost) {
        // 找到主机所属的 domain 和 os
        const hostDomain = hostList.find(domain =>
          domain.os.some(os => os.hosts.some(host => host.hostId === targetHost.hostId)),
        )

        if (hostDomain) {
          const hostOs = hostDomain.os.find(os =>
            os.hosts.some(host => host.hostId === targetHost.hostId),
          )

          if (hostOs) {
            // 设置完整的路径 [domainId, osType, hostId]
            form.setFieldsValue({
              agentToHost: {
                hostId: [hostDomain.id, hostOs.osSystem, targetHost.hostId],
              },
            })

            // 发现安全设备对应表单已有值，不做任何处理
            const currentDeviceIds = form.getFieldValue('deviceIds')
            if (currentDeviceIds && currentDeviceIds.length > 0) {
              return
            }

            try {
              const res = await getDeviceList(targetHost.hostId)

              if (!res || res.data.deviceList.length === 0) return
              // 发现默认安全设备有多个，不做任何处理
              if (res.data.deviceList.length > 1) {
                return
              }

              // 发现默认安全设备只有一个，给表单赋值
              if (res.data.deviceList.length === 1) {
                const defaultDevice = res.data.deviceList[0]

                // 找到设备所属的 deviceType
                const deviceType = res.data.deviceList.find(type =>
                  type.devices.some(
                    device => device.deviceId === defaultDevice.devices[0].deviceId,
                  ),
                )
                if (
                  deviceType &&
                  deviceType.devices[0].deviceStatus === 'DatasourceClientStatusOnline'
                ) {
                  form.setFieldsValue({
                    deviceIds: [deviceType.deviceType, defaultDevice.devices[0].deviceId],
                  })
                }
              }
            } catch (error) {}
          }
        }
      }
    }
  }

  // Host 变化
  const handleHostChange = async (value: (string | number)[]) => {
    if (!value || value.length < 3) return

    const hostId = value[2]

    // 找到选中的主机
    const selectedHost = hostList
      .flatMap(domain => domain.os)
      .flatMap(os => os.hosts)
      .find(host => host.hostId === hostId)

    if (selectedHost && selectedHost.agentId) {
      // 找到对应的 agent
      const targetAgent = agentList
        .flatMap(domain => domain.os)
        .flatMap(os => os.agents)
        .find(agent => agent.agentId === selectedHost.agentId)

      if (targetAgent) {
        // 找到 agent 所属的 domain 和 os
        const agentDomain = agentList.find(domain =>
          domain.os.some(os => os.agents.some(agent => agent.agentId === targetAgent.agentId)),
        )

        if (agentDomain) {
          const agentOs = agentDomain.os.find(os =>
            os.agents.some(agent => agent.agentId === targetAgent.agentId),
          )

          if (agentOs) {
            // 设置完整的路径 [domainId, osType, agentId]
            const agentPath = [agentDomain.id, agentOs.osSystem, targetAgent.agentId]

            // 设置 agentToHost 的 agentId
            form.setFieldsValue({
              agentToHost: {
                agentId: agentPath,
              },
            })

            // 如果验证模式包含 agentToListener，同步设置 agentToListener 的 agentId
            const currentExecuteMode = form.getFieldValue('executeMode')
            if (currentExecuteMode && currentExecuteMode.includes('agentToListener')) {
              form.setFieldsValue({
                agentToListener: {
                  agentId: agentPath,
                },
              })
            }
          }
        }
      }
    }

    // 发现安全设备对应表单已有值，不做任何处理
    const currentDeviceIds = form.getFieldValue('deviceIds')
    if (currentDeviceIds && currentDeviceIds.length > 0) {
      return
    }

    try {
      const res = await getDeviceList(hostId as string)

      if (!res || res.data.deviceList.length === 0) return

      // 发现默认安全设备有多个，不做任何处理
      if (res.data.deviceList.length > 1) {
        return
      }

      // 发现默认安全设备只有一个，给表单赋值
      if (res.data.deviceList.length === 1) {
        const defaultDevice = res.data.deviceList[0]
        // 找到设备所属的 deviceType
        const deviceType = res.data.deviceList.find(type =>
          type.devices.some(device => device.deviceId === defaultDevice.devices[0].deviceId),
        )

        if (deviceType) {
          form.setFieldsValue({
            deviceIds: [deviceType.deviceType, defaultDevice.devices[0].deviceId],
          })
        }
      }
    } catch (error) {}
  }

  // 处理模板数据的公共函数
  const processTemplateData = useCallback(
    (data: any[], isLatest: boolean) =>
      data.map(item => {
        const selection = templateSelectionMap[item.templateId]

        return {
          templateId: item.templateId,
          name: item.name,
          isUpdate: isLatest,
          resourceAmount: item.resourceAmount,
          resourceSelectedAmount:
            selection?.resourceSelectedAmount ?? item.resourceSelectedAmount ?? 0,
          originType: item.originType,
          updateTime: item.updateTime,
          usCaseIds: selection?.usCaseIds ?? [],
        }
      }),
    [templateSelectionMap],
  )

  // 模板列表搜索
  const handleSearch = useCallback(
    debounce(async (value: string) => {
      const newSearchParams = {
        ...templateSearchParams,
        filterParams: {
          ...templateSearchParams.filterParams,
          name: value,
        },
      }

      setTemplateSearchParams(newSearchParams)

      await fetchTemplateData(newSearchParams, processTemplateData, form, setTemplateList, setTotal)
    }, 300),
    [templateSearchParams, processTemplateData, form, fetchTemplateData],
  )

  // 获取排序参数的工具函数
  const getSortParams = useCallback(
    (sorter: SorterResult<TemplateList> | SorterResult<TemplateList>[]) => {
      let order = 'SortOrderUnspecified'
      let field: string | undefined = undefined

      if (!Array.isArray(sorter)) {
        order =
          sorter.order === 'descend'
            ? 'SortOrderDescending'
            : sorter.order === 'ascend'
            ? 'SortOrderAscending'
            : 'SortOrderUnspecified'
        field =
          sorter.field === 'resourceAmount'
            ? 'TemplateSortFieldResourceAmount'
            : sorter.field === 'updateTime'
            ? 'TemplateSortFieldUpdateTime'
            : 'TemplateSortFieldUnknown'
      }

      return { order, field }
    },
    [],
  )

  // 模板列表分页变化
  const handleTableChange = useCallback(
    async (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<TemplateList> | SorterResult<TemplateList>[],
    ) => {
      const { order, field } = getSortParams(sorter)

      const newSearchParams = {
        ...templateSearchParams,
        page: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
        filterParams: {
          ...templateSearchParams.filterParams,
          ...filters,
          sortOrder: order,
          sortField: field,
        },
      }

      setTemplateSearchParams(newSearchParams)

      await fetchTemplateData(newSearchParams, processTemplateData, form, setTemplateList, setTotal)
    },
    [templateSearchParams, getSortParams, processTemplateData, form, fetchTemplateData],
  )

  // 获取用例排序参数的工具函数
  const getUsCaseSortParams = useCallback(
    (sorter: SorterResult<UsCaseList> | SorterResult<UsCaseList>[]) => {
      let order = 'SortOrderUnspecified'
      let field: string | undefined = undefined

      if (!Array.isArray(sorter)) {
        order =
          sorter.order === 'descend'
            ? 'SortOrderDescending'
            : sorter.order === 'ascend'
            ? 'SortOrderAscending'
            : 'SortOrderUnspecified'
        field =
          sorter.field === 'updateTime' ? 'ResourceSortFieldUpdateTime' : 'ResourceSortFieldUnknown'
      }

      return { order, field }
    },
    [],
  )

  // 用例列表分页变化
  const handleUsCaseTableChange = useCallback(
    async (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<UsCaseList> | SorterResult<UsCaseList>[],
    ) => {
      const { order, field } = getUsCaseSortParams(sorter)

      const newSearchParams = {
        ...usCaseSearchParams,
        page: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
        filterParams: {
          ...usCaseSearchParams.filterParams,
          ...filters,
          sortOrder: order,
          sortField: field,
        },
        isSearch: true,
      }

      setUsCaseSearchParams(newSearchParams)

      await fetchUsCaseData(newSearchParams, setUsCaseList, setUsCaseTotal)
    },
    [usCaseSearchParams, getUsCaseSortParams, fetchUsCaseData],
  )

  // 执行更新变化
  const handleIsUpdateChange = (value: boolean, templateId: string) => {
    setTemplateList(prev =>
      prev.map(item => (item.templateId === templateId ? { ...item, isUpdate: value } : item)),
    )

    if (value) {
      setExecuteUpdateIds(prev => [...prev, templateId])
    } else {
      setExecuteUpdateIds(prev => prev.filter(eid => eid !== templateId))
    }
  }

  // 用例列表搜索类型变化
  const handleSearchTypeChange = (value: string) => {
    setSearchType(value)
  }

  // 用例列表搜索输入变化
  const handleSearchInputChange = useCallback(
    debounce(async (value: string) => {
      const newSearchParams = {
        ...usCaseSearchParams,
        page: 1,
        filterParams: {
          ...usCaseSearchParams.filterParams,
          name: value,
        },
        isSearch: true,
      }

      setUsCaseSearchParams(newSearchParams)

      await fetchUsCaseData(newSearchParams, setUsCaseList, setUsCaseTotal)
    }, 300),
    [usCaseSearchParams, fetchUsCaseData],
  )

  // 用例标签级联选择器变化
  const handleCascaderChange = useCallback(
    async (value: (string | number)[][] | (string | number)[]) => {
      setCascaderValue(value as (string | number)[])

      const processedValue = value
        .map(item => {
          if (item.length === 1) {
            return cascaderOptions[searchType]
              ?.find(tagCategory => tagCategory.value === item[0])
              ?.children.map(tag => tag.value)
              .flat()
          } else if (item.length === 2) {
            return item[1]
          }
        })
        .flat()

      const newSearchParams = {
        ...usCaseSearchParams,
        page: 1,
        isSearch: true,
        filterParams: {
          ...usCaseSearchParams.filterParams,
          [searchType]: processedValue,
        },
      }

      setUsCaseSearchParams(newSearchParams)

      await fetchUsCaseData(newSearchParams, setUsCaseList, setUsCaseTotal)
    },
    [usCaseSearchParams, searchType, cascaderOptions, fetchUsCaseData],
  )

  // 模板列表选中变化
  const handleSelectedRowKeys = async (
    record: TemplateList | { templateId: string[] },
    selected: boolean,
    isAll?: boolean,
  ) => {
    if (selected) {
      if (typeof record.templateId === 'string') {
        setUsCaseSearchParams(prev => ({
          ...prev,
          page: 1,
          templateId:
            typeof record.templateId === 'string'
              ? [...prev.templateId, record.templateId]
              : [...prev.templateId, ...record.templateId],
          isSearch: false,
        }))
      } else {
        if (isAll) {
          setUsCaseSearchParams(prev => ({
            ...prev,
            page: 1,
            templateId:
              typeof record.templateId === 'string'
                ? [...prev.templateId, record.templateId]
                : [...prev.templateId, ...record.templateId],
            isSearch: false,
          }))
        } else {
          setUsCaseSearchParams(prev => ({
            ...prev,
            page: 1,
            templateId:
              typeof record.templateId === 'string'
                ? [...prev.templateId, record.templateId]
                : record.templateId,
            isSearch: false,
          }))
        }
      }

      const newSearchParams = {
        ...usCaseSearchParams,
        templateId:
          typeof record.templateId === 'string'
            ? selectedRowKeys.concat(record.templateId)
            : record.templateId,
      }

      await fetchUsCaseData(newSearchParams, setUsCaseList, setUsCaseTotal)
    } else {
      const templateIdsToRemove =
        typeof record.templateId === 'string' ? [record.templateId] : record.templateId
      const usCaseIdsToRemove = templateList
        .filter(item => templateIdsToRemove.includes(item.templateId))
        .flatMap(item => item.usCaseIds)

      if (usCaseIdsToRemove.length > 0) {
        setSelectedUsCaseRowKeys(prev =>
          prev.filter(resourceId => !usCaseIdsToRemove.includes(resourceId)),
        )
      }

      if (templateIdsToRemove.length > 0) {
        setTemplateList(prev =>
          prev.map(item =>
            templateIdsToRemove.includes(item.templateId)
              ? { ...item, resourceSelectedAmount: 0, usCaseIds: [] }
              : item,
          ),
        )
        setExecuteUpdateIds(prev =>
          prev.filter(templateId => !templateIdsToRemove.includes(templateId)),
        )
        setTemplateSelectionMap(prev => {
          const updated = { ...prev }
          templateIdsToRemove.forEach(templateId => {
            delete updated[templateId]
          })
          return updated
        })
      }

      // 检查过滤后的数组是否为空
      const filteredKeys =
        typeof record.templateId === 'string'
          ? selectedRowKeys.filter(key => key !== record.templateId)
          : selectedRowKeys.filter(key => !record.templateId.includes(key))
      // 如果过滤后的数组为空，直接置空用例列表和总数
      if (filteredKeys.length === 0) {
        setSelectedRowKeys([])
        setExecuteUpdateIds([])
        setTemplateSelectionMap({})
        setUsCaseList([])
        setUsCaseTotal(0)
        setUsCaseSearchParams(prev => ({
          ...prev,
          page: 1,
          templateId: [],
          isSearch: false,
        }))
        return
      }

      // 更新用例总数
      if (typeof record.templateId === 'string') {
        setSelectedRowKeys(filteredKeys)
        setUsCaseSearchParams(prev => ({
          ...prev,
          page: 1,
          templateId: filteredKeys,
          isSearch: false,
        }))
      } else {
        setSelectedRowKeys(filteredKeys)
        if (isAll) {
          setUsCaseSearchParams(prev => ({
            ...prev,
            page: 1,
            templateId: filteredKeys,
            isSearch: false,
          }))
        } else {
          setUsCaseSearchParams(prev => ({
            ...prev,
            page: 1,
            templateId: filteredKeys,
            isSearch: false,
          }))
        }
      }

      const newSearchParams = {
        ...usCaseSearchParams,
        page: 1,
        templateId: filteredKeys,
      }

      await fetchUsCaseData(newSearchParams, setUsCaseList, setUsCaseTotal)
    }
  }

  // 全选当前页模板
  const handleSelectAllCurrentPageTemplate = (selected: boolean) => {
    const templateIds = templateList.map(item => item.templateId)
    const currentPageUsCaseNum = templateList.reduce((acc, item) => acc + item.resourceAmount, 0)
    if (selected) {
      handleSelectedRowKeys({ templateId: templateIds }, true)
    } else {
      handleSelectedRowKeys({ templateId: templateIds }, false)
      setUsCaseTotal(usCaseTotal - currentPageUsCaseNum)
    }
  }

  // 全选所有模板
  const handleSelectAllTemplate = e => {
    if (e.target.checked) {
      setSelectedRowKeys(caseTemplateIdsAll)
      handleSelectedRowKeys({ templateId: caseTemplateIdsAll }, true, true)
    } else {
      setSelectedRowKeys([])
      // 清理所有相关的用例数据
      setUsCaseList([])
      setSelectedUsCaseRowKeys([])
      setUsCaseTotal(0)
      setTemplateSelectionMap({})
      setTemplateList(prev =>
        prev.map(item => ({ ...item, resourceSelectedAmount: 0, usCaseIds: [] })),
      )
    }
  }

  // 用例列表选中变化
  const handleSelectedUsCaseRowKeys = (record: UsCaseList, selected: boolean) => {
    if (selected) {
      // 更新选中用例ids
      setSelectedUsCaseRowKeys(prev => [...prev, record.resourceId])
      // 更新模板列表下已选用的用例ids
      setTemplateList(prev =>
        prev.map(item =>
          record.templateIds?.includes(item.templateId)
            ? {
                ...item,
                resourceSelectedAmount: item.resourceSelectedAmount + 1,
                usCaseIds: [...item.usCaseIds, record.resourceId],
              }
            : item,
        ),
      )
      setTemplateSelectionMap(prev => {
        const updated = { ...prev }
        record.templateIds?.forEach(templateId => {
          if (!templateId) return
          const current = updated[templateId] || { resourceSelectedAmount: 0, usCaseIds: [] }
          if (current.usCaseIds.includes(record.resourceId)) {
            updated[templateId] = current
          } else {
            updated[templateId] = {
              resourceSelectedAmount: current.resourceSelectedAmount + 1,
              usCaseIds: [...current.usCaseIds, record.resourceId],
            }
          }
        })
        return updated
      })
    } else {
      setSelectedUsCaseRowKeys(prev => prev.filter(key => key !== record.resourceId))
      // 更新模板列表下已选用的用例ids
      setTemplateList(prev =>
        prev.map(item =>
          record.templateIds?.includes(item.templateId)
            ? {
                ...item,
                resourceSelectedAmount: item.resourceSelectedAmount - 1,
                usCaseIds: item.usCaseIds.filter(key => key !== record.resourceId),
              }
            : item,
        ),
      )
      setTemplateSelectionMap(prev => {
        const updated = { ...prev }
        record.templateIds?.forEach(templateId => {
          if (!templateId) return
          const current = updated[templateId]
          if (!current) return
          const nextUsCaseIds = current.usCaseIds.filter(
            useCaseId => useCaseId !== record.resourceId,
          )
          const nextCount = Math.max(current.resourceSelectedAmount - 1, 0)
          updated[templateId] = {
            resourceSelectedAmount: nextCount,
            usCaseIds: nextUsCaseIds,
          }
        })
        return updated
      })
    }
  }

  // 处理当页用例全选/取消全选逻辑
  const handleSelectAllCurrentPageUsCase = (selected: boolean) => {
    if (selected) {
      // 全选
      usCaseList.forEach(record => {
        handleSelectedUsCaseRowKeys(record, true)
      })
    } else {
      // 取消全选
      usCaseList.forEach(record => {
        handleSelectedUsCaseRowKeys(record, false)
      })
    }
  }

  // 处理 用例 全选/取消全选逻辑
  const handleSelectedUsCaseSelectAll = async (selected: boolean) => {
    if (selected) {
      // 更新选中用例ids
      setSelectedUsCaseRowKeys(usCaseIdsAll)
      if (usCaseIdsAll.length === 0) return

      try {
        const { isSearch: ignoredIsSearch, pageSize, ...restParams } = usCaseSearchParams
        const res = await getUsCaseList({
          ...restParams,
          page: 1,
          pageSize: usCaseTotal > 0 ? usCaseTotal : pageSize || 10,
        })
        const allUsCases: UsCaseList[] = res?.data?.list || []
        const selectedIdSet = new Set(usCaseIdsAll)
        const aggregatedSelection = new Map<string, string[]>()

        allUsCases.forEach(item => {
          if (!selectedIdSet.has(item.resourceId)) return
          item.templateIds?.forEach(templateId => {
            if (!templateId) return
            const current = aggregatedSelection.get(templateId) || []
            aggregatedSelection.set(templateId, [...current, item.resourceId])
          })
        })

        setTemplateSelectionMap(() => {
          const updated: Record<string, { resourceSelectedAmount: number; usCaseIds: string[] }> =
            {}
          aggregatedSelection.forEach((ids, templateId) => {
            updated[templateId] = { resourceSelectedAmount: ids.length, usCaseIds: ids }
          })
          return updated
        })

        setTemplateList(prev =>
          prev.map(item => {
            const selectedIds = aggregatedSelection.get(item.templateId) || []
            return {
              ...item,
              resourceSelectedAmount: selectedIds.length,
              usCaseIds: selectedIds,
            }
          }),
        )
      } catch (error) {
        console.error('Failed to sync template selection when selecting all use cases:', error)
      }
    } else {
      setSelectedUsCaseRowKeys([])
      // 更新模板列表下已选用的用例ids
      setTemplateSelectionMap({})
      setTemplateList(prev =>
        prev.map(item => ({
          ...item,
          resourceSelectedAmount: 0,
          usCaseIds: [],
        })),
      )
    }
  }

  const onFinish = async (values: any) => {
    if (taskType === 'scheduler_type_reserve' && !values.timeOfDay) {
      Message.error(t('请选择执行时间'))
      return
    }

    if (taskType === 'periodic') {
      if (values.periodType === 'scheduler_type_daily') {
        if (!values.timeOfDay) {
          Message.error(t('请选择执行时间'))
          return
        }
      } else if (values.periodType === 'scheduler_type_weekly') {
        if (!values.dayOfWeek || !values.timeOfDay) {
          Message.error(t('请选择执行时间'))
          return
        }
      } else if (values.periodType === 'scheduler_type_monthly') {
        if (!values.dayOfMonth || !values.timeOfDay) {
          Message.error(t('请选择执行时间'))
          return
        }
      }
    }

    let params = {}
    if (verificationType === 'VerificationTypeOffline') {
      params = {
        name: values.name,
        groupId: values.groupId,
        verificationType: 'VerificationTypeOffline',
        arch: values.agentToHost.agentId?.length > 0 ? values.agentToHost.agentId[0] : undefined,
        os: values.agentToHost.agentId?.length > 0 ? values.agentToHost.agentId[1] : undefined,
        schedulerType: 'scheduler_type_immediate',
        reduceSpeed: values.reduceSpeed,
      }
    } else {
      params = {
        name: values.name,
        cycleDays: values.cycleDays,
        executionTimes: values.executionTimes,
        groupId: values.groupId,
        verificationType: 'VerificationTypeOnline',
        arch: values.arch || undefined,
        os: values.os || undefined,
        reduceSpeed: values.reduceSpeed,
        priority: values.priority,
        deviceIds: values?.deviceIds?.length > 0 ? [values.deviceIds[1]] : undefined,
        taskTime: taskType === 'scheduler_type_reserve' ? values.timeOfDay : undefined,
        cronSchedule: {
          dayOfWeek: values.dayOfWeek || undefined,
          dayOfMonth: values.dayOfMonth || undefined,
          timeOfDay: values.timeOfDay ? moment(values.timeOfDay).format('HH:mm:ss') : undefined,
        },
        schedulerType: taskType === 'periodic' ? values.periodType : taskType,
        reportConfig: {
          send: values.send,
          emailList:
            values.emailList?.map(emailId => ({
              id: emailId,
              name: userEmailList.find(item => item.id === emailId)?.name,
              email: userEmailList.find(item => item.id === emailId)?.email,
            })) || undefined,
          filter: values.filter,
          condition: values.condition ? [values.condition] : undefined,
        },
        agentToHost: {
          agentId:
            values?.agentToHost?.agentId?.length > 0 ? values?.agentToHost?.agentId[2] : undefined,
          hostId:
            values?.agentToHost?.hostId?.length > 0 ? values?.agentToHost?.hostId[2] : undefined,
        },
        agentToListener: {
          agentId:
            values?.agentToListener?.agentId?.length > 0
              ? values?.agentToListener?.agentId[2]
              : undefined,
          listenerHostId:
            values?.agentToListener?.hostId?.length > 0
              ? values?.agentToListener?.hostId[2]
              : undefined,
          listenerHostIp: values?.agentToListener?.hostId?.length > 0 ? listenerHostIp : undefined,
          nat: values?.agentToListener?.nat || false,
          dport: values?.agentToListener?.dport || 1,
          dnatIp: values?.agentToListener?.dnatIp || undefined,
          dnatPort: values?.agentToListener?.dnatPort || undefined,
        },
      }
    }
    setCreateParams(params)
    handleNext()
  }

  const handleCreateTask = async () => {
    if (selectedRowKeys.length === 0) {
      Message.error(t('请选择模板'))
      return
    }

    if (selectedUsCaseRowKeys.length === 0) {
      Message.error(t('请选择用例'))
      return
    }

    try {
      const res = await createTask({
        ...createParams,
        templateList: selectedRowKeys.map(item => ({
          id: item,
          useLatestResource: executeUpdateIds.includes(item),
        })),
        resourceIds: selectedUsCaseRowKeys,
      })

      if (!res || !res.data) return

      Message.success(t('创建任务成功'))
      navigate('/validation/network/endpointsecurity?tab=task')
    } catch (error) {
      console.log(error)
    }
  }

  const fetchTemplateList = async () => {
    try {
      const res = await getCaseTemplateList(templateSearchParams)
      await getCaseTemplateIdsAll({
        ...templateSearchParams,
        page: undefined,
        pageSize: undefined,
        os: undefined,
      }).catch(() => {})
      const isLatest = form.getFieldValue('isLatest')
      const newTemplateList = res.data.list.map(item => ({
        templateId: item.templateId,
        name: item.name,
        isUpdate: isLatest,
        resourceAmount: item.resourceAmount,
        resourceSelectedAmount: isAllSelected
          ? item.resourceAmount
          : item.resourceSelectedAmount || 0,
        originType: item.originType,
        updateTime: item.updateTime,
        usCaseIds: [],
      }))

      setTemplateList(newTemplateList)
      setTotal(res.data.total)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchUsCaseList = async () => {
    await fetchUsCaseData(usCaseSearchParams, setUsCaseList, setUsCaseTotal)
  }

  useEffect(() => {
    if (usCaseIdsAll.length > 0) {
      getUseCaseTagList({
        ...usCaseSearchParams,
        page: undefined,
        pageSize: undefined,
        isSearch: undefined,
      }).catch(_ => {})
    }
  }, [usCaseIdsAll])

  useEffect(() => {
    if (step === 2 && templateList.length === 0) {
      fetchTemplateList()
    }
  }, [templateSearchParams])

  useEffect(() => {
    if (step === 2 && usCaseSearchParams.isSearch) {
      fetchUsCaseList()
    }
  }, [usCaseSearchParams, step, selectedRowKeys])

  // 查找Agent在agentList中的完整路径
  const findAgentPath = useCallback(
    (agentId: string) => {
      for (const domain of agentList) {
        for (const os of domain.os) {
          const foundAgent = os.agents.find(agent => agent.agentId === agentId)
          if (foundAgent) {
            return {
              domainId: domain.id,
              osSystem: os.osSystem,
              agentId: foundAgent.agentId,
              path: [domain.id, os.osSystem, foundAgent.agentId],
            }
          }
        }
      }
      return null
    },
    [agentList],
  )

  // 查找Host在hostList中的完整路径
  const findHostPath = useCallback(
    (hostId: string) => {
      for (const domain of hostList) {
        for (const os of domain.os) {
          const foundHost = os.hosts.find(host => host.hostId === hostId)
          if (foundHost) {
            return {
              domainId: domain.id,
              osSystem: os.osSystem,
              hostId: foundHost.hostId,
              path: [domain.id, os.osSystem, foundHost.hostId],
            }
          }
        }
      }
      return null
    },
    [hostList],
  )

  // 设置设备信息
  const setDeviceInfo = useCallback(
    async (hostId: string) => {
      const currentDeviceIds = form.getFieldValue('deviceIds')
      if (currentDeviceIds && currentDeviceIds.length > 0) {
        return
      }

      try {
        const response = await getDeviceList(hostId)
        if (!response || response.data.deviceList.length !== 1) {
          return
        }

        const defaultDevice = response.data.deviceList[0]
        const deviceType = response.data.deviceList.find(type =>
          type.devices.some(device => device.deviceId === defaultDevice.devices[0].deviceId),
        )

        if (deviceType) {
          form.setFieldsValue({
            deviceIds: [deviceType.deviceType, defaultDevice.devices[0].deviceId],
          })
        }
      } catch (error) {
        console.error('Failed to get device list:', error)
      }
    },
    [form, getDeviceList],
  )

  // 设置验证模式配置
  const setupVerificationMode = useCallback(
    async (modeData: any, modeType: 'agentToHost' | 'agentToListener') => {
      if (!modeData.agentId || modeData.agentId.length === 0) {
        return false
      }

      const agentPath = findAgentPath(modeData.agentId)
      if (!agentPath) {
        return false
      }

      // 设置Agent信息
      form.setFieldsValue({
        [modeType]: {
          agentId: agentPath.path,
        },
      })

      // 查找关联的主机
      const targetHost = hostList
        .flatMap(domain => domain.os)
        .flatMap(os => os.hosts)
        .find(host => host.agentId === modeData.agentId)

      if (targetHost) {
        const hostPath = findHostPath(targetHost.hostId)
        if (hostPath) {
          const hostConfig: any = {
            hostId: hostPath.path,
          }

          // 为agentToListener模式添加额外配置
          if (modeType === 'agentToListener') {
            hostConfig.dport = modeData.dport

            if (modeData.nat) {
              setNat(true)
              form.setFieldValue('agentToListener.nat', true)
              form.setFieldValue('agentToListener.dnatIp', modeData.dnatIp)
              form.setFieldValue('agentToListener.dnatPort', modeData.dnatPort)
            }
          }

          form.setFieldsValue({
            [modeType]: {
              ...form.getFieldValue(modeType),
              ...hostConfig,
            },
          })

          // 设置设备信息
          await setDeviceInfo(targetHost.hostId)
        }
      }

      return true
    },
    [form, findAgentPath, findHostPath, hostList, setDeviceInfo, setNat],
  )

  // 设置调度器配置
  const setupSchedulerConfig = useCallback(
    (schedulerData: any) => {
      const { schedulerType, dayOfWeek, dayOfMonth, executionTimes, isLatest, cronSchedule } =
        schedulerData

      switch (schedulerType) {
        case 'scheduler_type_immediate':
          form.setFieldValue('schedulerType', 'scheduler_type_immediate')
          break
        case 'scheduler_type_reserve':
          form.setFieldValue('schedulerType', 'scheduler_type_reserve')
          // 如果有时间数据，转换为moment对象
          if (cronSchedule?.timeOfDay) {
            form.setFieldValue('timeOfDay', moment(cronSchedule.timeOfDay, 'HH:mm:ss'))
          }
          break
        case 'scheduler_type_daily':
          form.setFieldValue('schedulerType', 'periodic')
          form.setFieldValue('periodType', 'scheduler_type_daily')
          form.setFieldValue('executionTimes', executionTimes)
          if (cronSchedule?.timeOfDay) {
            form.setFieldValue('timeOfDay', moment(cronSchedule.timeOfDay, 'HH:mm:ss'))
          }
          break
        case 'scheduler_type_weekly':
          form.setFieldValue('schedulerType', 'periodic')
          form.setFieldValue('periodType', 'scheduler_type_weekly')
          form.setFieldValue('dayOfWeek', dayOfWeek)
          form.setFieldValue('executionTimes', executionTimes)
          if (cronSchedule?.timeOfDay) {
            form.setFieldValue('timeOfDay', moment(cronSchedule.timeOfDay, 'HH:mm:ss'))
          }
          break
        case 'scheduler_type_monthly':
          form.setFieldValue('schedulerType', 'periodic')
          form.setFieldValue('periodType', 'scheduler_type_monthly')
          form.setFieldValue('dayOfMonth', dayOfMonth)
          form.setFieldValue('executionTimes', executionTimes)
          form.setFieldValue('isLatest', isLatest)
          if (cronSchedule?.timeOfDay) {
            form.setFieldValue('timeOfDay', moment(cronSchedule.timeOfDay, 'HH:mm:ss'))
          }
          break
        default:
          break
      }
    },
    [form],
  )

  // 设置报表配置
  const setupReportConfig = useCallback(
    (reportConfig: any) => {
      if (!reportConfig) return

      form.setFieldValue('send', reportConfig.send)
      setSend(reportConfig.send)
      form.setFieldValue('filter', reportConfig.filter)
      setFilter(reportConfig.filter)
      form.setFieldValue(
        'emailList',
        reportConfig.emailList.map((item: any) => item.id),
      )
      form.setFieldValue('condition', [reportConfig.condition])
    },
    [form, setSend, setFilter],
  )

  // 任务复制功能
  useEffect(() => {
    if (agentList.length === 0 || hostList.length === 0 || !id) return

    const loadTaskData = async () => {
      try {
        const res = await getTaskBaseInfo(id)
        const taskData = res.data
        const verificationMode: string[] = []

        // 设置agentToHost模式
        if (await setupVerificationMode(taskData.verificationMode.agentToHost, 'agentToHost')) {
          verificationMode.push('agentToHost')
        }

        // 设置agentToListener模式
        if (
          await setupVerificationMode(taskData.verificationMode.agentToListener, 'agentToListener')
        ) {
          verificationMode.push('agentToListener')
        }

        // 设置基本表单信息
        form.setFieldsValue({
          name: taskData.name,
          groupId: taskData.groupId,
          verificationType: taskData.verificationType,
          executeMode: verificationMode,
          schedulerType: taskData.schedulerType,
          reduceSpeed: taskData.sampleDeliveryRate || 10,
          priority: taskData.priority,
        })

        // 设置执行模式
        setExecuteMode(verificationMode)

        // 设置调度器配置
        setupSchedulerConfig(taskData)

        // 设置报表配置
        setupReportConfig(taskData.reportConfig)
      } catch (error) {
        console.error('Failed to load task data:', error)
      }
    }

    loadTaskData()
  }, [
    id,
    agentList,
    hostList,
    setupVerificationMode,
    setupSchedulerConfig,
    setupReportConfig,
    setExecuteMode,
  ])

  useEffect(() => {
    getTaskGroup({ page: 1, limit: 10000 })
      .then(res => {
        form.setFieldsValue({
          groupId: res.data.groups.find(item => item.groupName === '常规任务')?.groupId,
        })
      })
      .catch(() => {})
    getAgentList().catch(() => {})
    getHostList().catch(() => {})
    getUserEmailList().catch(() => {})
    getAuthorizeConfig().catch(() => {})

    return () => {
      setStep(1)
      setTaskType('scheduler_type_immediate')
      setVerificationType('VerificationTypeOnline')
      setExecuteMode(['agentToHost'])
      setNat(false)
      setSend(false)
      setFilter(false)
      setTotal(0)
      setTemplateList([])
      setTemplateSelectionMap({})
      setExecuteUpdateIds([])
      setUsCaseTotal(0)
      setUsCaseList([])
      setSelectedRowKeys([])
      setSelectedUsCaseRowKeys([])
      setSearchValue('')
      setSearchType('name')
      setUsCaseSearchValue('')
      setCascaderValue([])
      setTemplateSearchParams({
        page: 1,
        pageSize: 10,
        filterParams: {
          verificationModeIds: [4, 9],
          sceneIds: [9],
        },
      })
      setUsCaseSearchParams({
        page: 1,
        pageSize: 10,
        templateId: [],
        queryType: 'query_bind',
        filterParams: {
          verificationModeId: [4, 9],
          sceneId: [9],
        },
      })
      setDetailDrawer({ visible: false, id: '' })
      setAddTemplateModal({ visible: false, ids: [] })
    }
  }, [])

  return (
    <Wrapper>
      <div className="container">
        <div className="header">
          <div className="title">{t('创建任务')}</div>
          <div className="steps-container">
            <div className={step === 1 ? 'step-number active' : 'step-number'}>01</div>
            <div className={step === 1 ? 'step-number' : 'step-number active'}>02</div>
            <div className="step-icon-wrapper">
              <SvgIcon className="step-icon" width="40px" height="40px" name="任务配置" />
              <span className={step === 1 ? 'step-text active' : 'step-text'}>{t('任务配置')}</span>
            </div>
            <div className="step-arrow">
              <SvgIcon
                width="24"
                height="28"
                name={step === 1 ? 'step-arrow-normal' : 'step-arrow-active'}
              />
            </div>
            <div className="step-icon-wrapper">
              <SvgIcon
                className="step-icon"
                width="40px"
                height="40px"
                name={step === 1 ? '模版及用例选择灰' : '模版及用例选择'}
              />

              <span className={step === 1 ? 'step-text' : 'step-text active'}>
                {t('模板及用例选择')}
              </span>
            </div>
          </div>
        </div>
        <div className="content">
          {step === 1 && (
            <Form
              form={form}
              onFinish={onFinish}
              colon={false}
              initialValues={{
                verificationType: 'VerificationTypeOnline',
                executeMode: ['agentToHost'],
                schedulerType: 'scheduler_type_immediate',
                periodType: 'scheduler_type_daily',
                reduceSpeed: 10,
                priority: 3,
                isLatest: false,
              }}
            >
              {/* 任务基本信息 */}
              <div className="section">
                <div className="section-title">
                  {t('任务基本信息')}
                  <div className="section-bar" />
                </div>
                <div className="form-content">
                  <Form.Item
                    label={t('任务名称')}
                    name="name"
                    rules={[
                      { required: true, message: t('请输入任务名称') },
                      { max: 50, message: t('任务名称不能超过50个字符') },
                    ]}
                  >
                    <Input placeholder={t('请输入任务名称')} style={{ width: '360px' }} />
                  </Form.Item>
                  <Form.Item label={t('任务分组')} name="groupId">
                    <Select
                      placeholder={t('请选择任务分组')}
                      options={taskGroup.map(item => ({
                        label: item.groupName,
                        value: item.groupId,
                      }))}
                      style={{ width: '360px' }}
                    />
                  </Form.Item>
                </div>
              </div>

              {/* 任务验证配置 */}
              <div className="section">
                <div className="section-title">
                  {t('任务验证配置')}
                  <div className="section-bar" />
                </div>
                <div className="form-content">
                  <Form.Item
                    label={
                      <div className="custom-label">
                        <span>{t('验证类型')}</span>
                      </div>
                    }
                    name="verificationType"
                  >
                    <Select
                      value={verificationType}
                      onChange={value => {
                        setVerificationType(value)
                        if (value === 'VerificationTypeOffline') {
                          setExecuteMode(['agentToHost', 'agentToListener'])
                          form.setFieldValue('agentToHost', undefined)
                          form.setFieldValue('agentToListener', undefined)
                        } else {
                          setExecuteMode(['agentToHost'])
                          form.setFieldValue('agentToHost', undefined)
                          form.setFieldValue('agentToListener', undefined)
                        }
                      }}
                      placeholder={t('请选择任务验证类型')}
                      options={verificationTypeOptions}
                      style={{ width: '360px' }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('验证模式')}
                    name="executeMode"
                    valuePropName="checked"
                    rules={[{ required: true, message: t('请选择验证模式') }]}
                  >
                    <Checkbox.Group
                      value={executeMode}
                      onChange={(e: CheckboxValueType[]) => handleExecuteModeChange(e)}
                    >
                      <Checkbox
                        value="agentToHost"
                        disabled={verificationType === 'VerificationTypeOffline'}
                        style={{ marginRight: 60 }}
                      >
                        {t('Agent → 主机')}
                      </Checkbox>
                      <Checkbox
                        value="agentToListener"
                        disabled={verificationType === 'VerificationTypeOffline'}
                      >
                        Agent → Listener
                        <Tooltip
                          title={t(
                            '评估验证从agent发起攻击，至另一个Agent进行验证，评估该路径中安全设备的评估情况',
                          )}
                        >
                          <IconFont
                            type="icon-icon_help"
                            style={{
                              color: '#999',
                              fontSize: 16,
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      </Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                  {executeMode.includes('agentToHost') && (
                    <div className="config-section">
                      {verificationType !== 'VerificationTypeOffline' && (
                        <div className="config-title">
                          <div>
                            <span style={{ marginLeft: 14, marginRight: 50 }}>{t('配置模式')}</span>
                            <span>
                              Agent <span style={{ color: '#4238C9' }}>→</span> {t('主机')}
                            </span>
                          </div>
                        </div>
                      )}
                      <Form.Item label={t('攻击主体')} style={{ fontWeight: 400 }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Form.Item
                            className="agent-to-host-agent-id-form-item"
                            label={<span>Agent</span>}
                            name={['agentToHost', 'agentId']}
                            style={{ marginLeft: 10, marginBottom: 0 }}
                            rules={[{ required: true, message: t('请选择Agent') }]}
                          >
                            <Cascader
                              options={agentListOptions}
                              placeholder={t('请选择Agent')}
                              style={{ width: 640, marginLeft: 16 }}
                              displayRender={
                                verificationType === 'VerificationTypeOnline'
                                  ? agentDisplayRender
                                  : undefined
                              }
                              onChange={e => handleAgentChange(e)}
                            />
                          </Form.Item>
                          <Button
                            type="link"
                            onClick={() => navigate('/configure/client/list')}
                            style={{ marginLeft: 8, padding: '4px 0' }}
                          >
                            {t('添加')}
                          </Button>
                        </div>
                      </Form.Item>
                      {verificationType === 'VerificationTypeOnline' && (
                        <Form.Item label={t('目标主体')}>
                          <Input.Group compact>
                            <Form.Item
                              label={<span>{t('验证主机')}</span>}
                              name={['agentToHost', 'hostId']}
                              style={{ marginLeft: 28 }}
                              rules={[{ required: true, message: t('请选择目标主体') }]}
                            >
                              <Cascader
                                options={hostListOptions}
                                placeholder={t('请选择主机')}
                                style={{ width: 640 }}
                                displayRender={hostDisplayRender}
                                onChange={e => handleHostChange(e)}
                              />
                            </Form.Item>
                          </Input.Group>
                        </Form.Item>
                      )}
                    </div>
                  )}

                  {verificationType === 'VerificationTypeOnline' &&
                    executeMode.includes('agentToListener') && (
                      <div className="config-section">
                        <div className="config-title">
                          <div>
                            <span style={{ marginLeft: 14, marginRight: 50 }}>{t('配置模式')}</span>
                            <span>
                              Agent <span style={{ color: '#4238C9' }}>→</span> Listener
                            </span>
                          </div>
                          <Form.Item
                            label={t('NAT模式')}
                            name={['agentToListener', 'nat']}
                            valuePropName="checked"
                          >
                            <BasSwitch checked={nat} onChange={e => setNat(e)} />
                          </Form.Item>
                        </div>
                        <Form.Item label={t('攻击主体')}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Form.Item
                              className="agent-to-listener-agent-id-form-item"
                              label={<span>Agent</span>}
                              name={['agentToListener', 'agentId']}
                              style={{ marginLeft: 10, marginBottom: 0, flex: 1 }}
                              rules={[{ required: true, message: t('请选择Agent') }]}
                            >
                              <Cascader
                                options={agentListOptions}
                                placeholder={t('请选择Agent')}
                                style={{ width: 700, marginLeft: 16 }}
                                displayRender={agentDisplayRender}
                                onChange={e => handleAgentChange(e)}
                              />
                            </Form.Item>
                            <Button
                              type="link"
                              onClick={() => navigate('/configure/client/list')}
                              style={{ marginLeft: 8, padding: '4px 0' }}
                            >
                              {t('添加')}
                            </Button>
                          </div>
                        </Form.Item>
                        <Form.Item label={t('目标主体')}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Form.Item
                              label={<span>Listener</span>}
                              name={['agentToListener', 'hostId']}
                              style={{ marginLeft: 26, marginBottom: 0 }}
                              rules={[
                                { required: true, message: t('请选择目标主体') },
                                {
                                  validator: (_, value) => {
                                    if (!value || value.length !== 4) {
                                      return Promise.reject(new Error(t('请选择主机和IP地址')))
                                    }
                                    return Promise.resolve()
                                  },
                                },
                              ]}
                            >
                              <Cascader
                                options={listenerListOptions}
                                placeholder={t('请选择主机')}
                                displayRender={listenerDisplayRender}
                                style={{ width: 455 }}
                                onChange={e => setListenerHostIp(e[3] as string)}
                              />
                            </Form.Item>
                            <Form.Item
                              label={<span>PORT</span>}
                              name={['agentToListener', 'dport']}
                              style={{ marginLeft: 8 }}
                              rules={[{ required: true, message: t('请选择PORT') }]}
                            >
                              <Select
                                placeholder="PORT"
                                style={{ width: 160 }}
                                virtual={false}
                                options={[
                                  { label: '23001', value: 23001 },
                                  { label: '23002', value: 23002 },
                                  { label: '23003', value: 23003 },
                                  { label: '23004', value: 23004 },
                                  { label: '23005', value: 23005 },
                                  { label: '23006', value: 23006 },
                                  { label: '23007', value: 23007 },
                                  { label: '23008', value: 23008 },
                                  { label: '23009', value: 23009 },
                                  { label: '23010', value: 23010 },
                                ]}
                              />
                            </Form.Item>
                          </div>
                        </Form.Item>
                        {nat && (
                          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 94 }}>
                            <Form.Item
                              label={<span>DNAT</span>}
                              name={['agentToListener', 'dnatIp']}
                              style={{ marginBottom: 0 }}
                              rules={[
                                {
                                  required: true,
                                  message: t('请输入DNAT'),
                                },
                                {
                                  pattern: ipPattern,
                                  message: t('请输入正确的IP地址'),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t('请输入')}
                                style={{ width: nat ? 410 : 700, marginLeft: '12px' }}
                              />
                            </Form.Item>
                            <Form.Item
                              label={<span>DPORT</span>}
                              name={['agentToListener', 'dnatPort']}
                              style={{ marginLeft: 0 }}
                              rules={[
                                {
                                  required: true,
                                  message: t('请输入DPORT'),
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="DPORT"
                                style={{ width: 160 }}
                                min={1}
                                max={65535}
                              />
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    )}

                  {verificationType === 'VerificationTypeOnline' && (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 78 }}>
                      <span style={{ marginRight: 10 }}>{t('安全设备')}</span>
                      <Form.Item name="deviceIds" style={{ marginBottom: 0 }}>
                        <Cascader
                          options={deviceListOptions}
                          placeholder={t('请选择安全设备')}
                          style={{ width: 760, marginLeft: 10 }}
                          displayRender={deviceDisplayRender}
                        />
                      </Form.Item>
                      <Button
                        type="link"
                        onClick={() => window.open('/configure/device/access-management')}
                        style={{ marginLeft: 16, padding: '4px 0' }}
                      >
                        {t('添加接入设备')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* 任务执行配置 */}
              <div className="section">
                <div className="section-title">
                  {t('任务执行配置')}
                  <div className="section-bar" />
                </div>
                <Form.Item
                  label={t('执行类型')}
                  name="schedulerType"
                  required
                  style={{ marginLeft: 34, marginBottom: 0 }}
                >
                  <Radio.Group>
                    <Radio.Button value="scheduler_type_immediate">{t('即时任务')}</Radio.Button>
                    {verificationType === 'VerificationTypeOnline' && (
                      <>
                        <Radio.Button value="scheduler_type_reserve">{t('定时任务')}</Radio.Button>
                        <Radio.Button value="periodic">{t('周期任务')}</Radio.Button>
                      </>
                    )}
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.schedulerType !== currentValues.schedulerType
                  }
                >
                  {({ getFieldValue }) => {
                    const executeType = getFieldValue('schedulerType')
                    setTaskType(executeType)
                    if (executeType === 'periodic') {
                      return (
                        <>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginTop: 24,
                              marginBottom: 0,
                            }}
                          >
                            <Form.Item
                              label={<span style={{ color: '#666' }}>{t('周期配置')}</span>}
                              style={{ marginLeft: 100, marginBottom: 0 }}
                            >
                              <Input.Group compact>
                                <Form.Item name="periodType" noStyle>
                                  <Select
                                    style={{ width: 98 }}
                                    options={[
                                      { label: t('每月'), value: 'scheduler_type_monthly' },
                                      { label: t('每周'), value: 'scheduler_type_weekly' },
                                      { label: t('每天'), value: 'scheduler_type_daily' },
                                    ]}
                                    maxTagCount={2}
                                  />
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.periodType !== currentValues.periodType
                                  }
                                >
                                  {({ getFieldValue: getPeriodType }) => {
                                    const periodType = getPeriodType('periodType')
                                    if (periodType === 'scheduler_type_weekly') {
                                      return (
                                        <Form.Item name="dayOfWeek" noStyle>
                                          <Select
                                            mode="multiple"
                                            placeholder={t('选择星期')}
                                            style={{ width: 130 }}
                                            options={[
                                              { label: t('星期一'), value: 1 },
                                              { label: t('星期二'), value: 2 },
                                              { label: t('星期三'), value: 3 },
                                              { label: t('星期四'), value: 4 },
                                              { label: t('星期五'), value: 5 },
                                              { label: t('星期六'), value: 6 },
                                              { label: t('星期日'), value: 0 },
                                            ]}
                                            maxTagCount={1}
                                          />
                                        </Form.Item>
                                      )
                                    }
                                    if (periodType === 'scheduler_type_monthly') {
                                      return (
                                        <Form.Item name="dayOfMonth" noStyle>
                                          <Select
                                            mode="multiple"
                                            placeholder={t('选择日期')}
                                            style={{ width: 120 }}
                                            options={Array.from({ length: 31 }, (_, i) => ({
                                              label: `${i + 1}号`,
                                              value: i + 1,
                                            }))}
                                            maxTagCount={1}
                                          />
                                        </Form.Item>
                                      )
                                    }
                                    return null
                                  }}
                                </Form.Item>
                                <Form.Item name="timeOfDay" noStyle>
                                  <DatePicker.TimePicker
                                    placeholder={t('选择时间')}
                                    style={{ width: 200 }}
                                  />
                                </Form.Item>
                              </Input.Group>
                            </Form.Item>
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 10 }}>
                              <Form.Item
                                label={<span style={{ color: '#666' }}>{t('总执行次数')}</span>}
                                name="executionTimes"
                                style={{ marginBottom: 0 }}
                              >
                                <InputNumber
                                  min={1}
                                  max={999}
                                  placeholder={t('默认不限次数')}
                                  style={{ width: 120 }}
                                />
                              </Form.Item>
                              <span style={{ marginLeft: 8 }}>{t('次')}</span>
                            </div>
                          </div>
                        </>
                      )
                    }
                    if (executeType === 'scheduler_type_reserve') {
                      return (
                        <div style={{ marginTop: 24, marginBottom: 0 }}>
                          <Form.Item
                            name="timeOfDay"
                            label={<span style={{ color: '#666' }}>{t('执行时间')}</span>}
                            style={{ marginBottom: 0, marginLeft: 100 }}
                            rules={[{ required: true, message: t('请选择执行时间') }]}
                          >
                            <DatePicker
                              placeholder={t('请选择日期与时间')}
                              style={{
                                width: '240px',
                                fontSize: '14px',
                                borderRadius: '6px',
                              }}
                              showTime
                              disabledDate={disabledDate}
                              disabledTime={disabledDateTime}
                            />
                          </Form.Item>
                        </div>
                      )
                    }
                    return null
                  }}
                </Form.Item>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Form.Item
                    name="reduceSpeed"
                    label={
                      <div className="custom-label">
                        <span>{t('执行速率')}</span>
                      </div>
                    }
                    style={{ margin: '24px 0 0 34px' }}
                    rules={[
                      {
                        required: true,
                        message: t('请输入恶意样本投递速率'),
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 10,
                        message: t('请输入1-10的正整数'),
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={10}
                      style={{ width: 360 }}
                      precision={0}
                      parser={value => {
                        if (value === undefined || value === '') return ''
                        const parsed = parseInt(value)
                        return isNaN(parsed) ? '' : parsed
                      }}
                    />
                  </Form.Item>
                  <span style={{ margin: '24px  0 0 16px' }}>{t('个')}</span>
                </div>
                {verificationType === 'VerificationTypeOnline' && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Form.Item
                      name="priority"
                      label={
                        <div className="custom-label">
                          <span>{t('优先级')}</span>
                        </div>
                      }
                      style={{ marginLeft: 34, marginBottom: 0, marginTop: 24 }}
                    >
                      <InputNumber
                        min={1}
                        max={5}
                        style={{ width: 360 }}
                        precision={0}
                        parser={value => {
                          if (value === undefined || value === '') return ''
                          const parsed = parseInt(value)
                          return isNaN(parsed) ? '' : parsed
                        }}
                      />
                    </Form.Item>
                    <Tooltip title={t('数字越大，优先级越高')}>
                      <IconFont
                        type="icon-icon_help"
                        style={{
                          color: '#999',
                          fontSize: 16,
                          cursor: 'pointer',
                          marginLeft: 16,
                          marginTop: 24,
                        }}
                      />
                    </Tooltip>
                  </div>
                )}

                {taskType === 'periodic' && verificationType === 'VerificationTypeOnline' && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Form.Item
                      name="isLatest"
                      label={
                        <div className="custom-label">
                          <span>{t('执行增量')}</span>
                        </div>
                      }
                      valuePropName="checked"
                      style={{ marginLeft: 34, marginBottom: 0, marginTop: 24 }}
                    >
                      <BasSwitch style={{ marginLeft: 8 }} />
                    </Form.Item>
                    <Tooltip
                      title={t('启用后则对全选用例的模版周期执行时执行新添加到模版中的用例')}
                    >
                      <IconFont
                        type="icon-icon_help"
                        style={{
                          color: '#999',
                          fontSize: 16,
                          cursor: 'pointer',
                          marginLeft: 16,
                          marginTop: 24,
                        }}
                      />
                    </Tooltip>
                  </div>
                )}
              </div>

              {/* 任务高级配置 - 只在在线验证时显示 */}
              {verificationType === 'VerificationTypeOnline' && (
                <div className="section">
                  <div className="section-title">
                    {t('任务高级配置')}
                    <div className="section-bar" />
                  </div>
                  {/* 自动发送报表 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 24,
                      paddingLeft: 34,
                    }}
                  >
                    <Form.Item
                      label={
                        <div className="custom-label">
                          <span>{t('发送报表')}</span>
                        </div>
                      }
                      name="send"
                      valuePropName="checked"
                      style={{ marginBottom: 0, marginRight: 24 }}
                    >
                      <BasSwitch checked={send} onChange={setSend} style={{ marginLeft: 8 }} />
                    </Form.Item>
                    {send && (
                      <>
                        <span style={{ color: '#666', marginRight: 12 }}>{t('发送邮箱')}</span>
                        <Form.Item
                          name="emailList"
                          style={{ marginBottom: 0, width: 360 }}
                          rules={[{ required: true, message: t('请选择发送邮箱') }]}
                        >
                          <Select
                            mode="multiple"
                            showSearch
                            placeholder={t('人员名称/邮箱地址')}
                            style={{ width: 360 }}
                            options={userEmailList.map(item => ({
                              label: `${item.name}(${item.email})`,
                              value: item.id,
                            }))}
                          />
                        </Form.Item>
                      </>
                    )}
                  </div>
                  {/* 报表发送过滤 */}
                  {send && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 34,
                      }}
                    >
                      <Form.Item
                        label={
                          <div className="custom-label">
                            <span>{t('过滤配置')}</span>
                          </div>
                        }
                        name="filter"
                        valuePropName="checked"
                        style={{ marginBottom: 0, marginRight: 24 }}
                      >
                        <BasSwitch
                          checked={filter}
                          onChange={setFilter}
                          style={{ marginLeft: 8 }}
                        />
                      </Form.Item>
                      {filter && (
                        <>
                          <span style={{ color: '#666', marginRight: 12 }}>{t('过滤条件')}</span>
                          <Form.Item
                            name="condition"
                            style={{ marginBottom: 0 }}
                            rules={[{ required: true, message: t('请选择过滤条件') }]}
                          >
                            <Radio.Group className="radio-group">
                              <Radio value="conditionExistFailed">{t('任意用例防护失效')}</Radio>
                              <Radio value="conditionAllFailed">{t('全部用例防护失效')}</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Form>
          )}
          {step === 2 && (
            <div className="wrapper">
              <div className="template-wrapper">
                <div className="title">
                  <span>{t('模板选择')}</span>
                  <Input
                    prefix={
                      <IconFont type="icon-sousuo2" style={{ color: '#666', fontSize: '16px' }} />
                    }
                    placeholder={t('模板名称')}
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value)
                      handleSearch(e.target.value)
                    }}
                  />
                </div>
                <Checkbox
                  disabled={templateList.length === 0}
                  checked={selectedRowKeys.length > 0 && selectedRowKeys.length === total}
                  onChange={handleSelectAllTemplate}
                >
                  {t('全选所有')}
                </Checkbox>
                <div className="table-wrapper">
                  <Scrollbars>
                    <Table
                      loading={getCaseTemplateListLoading}
                      columns={columns}
                      dataSource={templateList}
                      bordered={false}
                      rowKey="templateId"
                      scroll={{ x: 300 }}
                      onChange={handleTableChange}
                      rowSelection={{
                        selectedRowKeys,
                        onChange: keys => setSelectedRowKeys(keys as string[]),
                        onSelect: (record, selected) => handleSelectedRowKeys(record, selected),
                        onSelectAll: selected => handleSelectAllCurrentPageTemplate(selected),
                      }}
                      pagination={{
                        total,
                        showTotal: totalCount => (
                          <span>
                            {t('共')}
                            <span style={{ color: '#4238c9' }}>{totalCount}</span>
                            {t('条')}
                          </span>
                        ),

                        pageSize: templateSearchParams.pageSize,
                        current: templateSearchParams.page,
                        pageSizeOptions: ['10', '20', '30', '40', '50'],
                        showSizeChanger: true,
                        showQuickJumper: true,
                      }}
                    />
                  </Scrollbars>
                </div>
              </div>
              <div className="case-wrapper">
                <div className="title">
                  <span>
                    {t('已选用例 (')}
                    {selectedUsCaseRowKeys.length})
                  </span>
                  <div className="right">
                    <Select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      options={[
                        { value: 'name', label: t('用例名称') },
                        { value: 'tagId', label: t('用例标签') },
                      ]}
                    />

                    {['name'].includes(searchType) ? (
                      <Input
                        placeholder={t('请输入搜索内容')}
                        value={usCaseSearchValue}
                        onChange={e => {
                          setUsCaseSearchValue(e.target.value)
                          if (selectedRowKeys.length === 0) return
                          handleSearchInputChange(e.target.value)
                        }}
                      />
                    ) : ['tagId'].includes(searchType) ? (
                      <Cascader
                        multiple
                        placeholder={t('请选择')}
                        value={cascaderValue}
                        onChange={handleCascaderChange}
                        options={cascaderOptions[searchType]}
                        className="custom-cascader"
                        maxTagCount={2}
                        showSearch
                        searchValue={searchValue}
                        onSearch={value => setSearchValue(value)}
                        {...(searchType === 'tagId'
                          ? {
                              displayRender: (_, selectedOptions = []) => {
                                const lastOption = selectedOptions[selectedOptions.length - 1]
                                if (!lastOption?.label) return ''
                                const labelElement = lastOption.label as React.ReactElement
                                return labelElement.props?.children[0]?.props?.children || ''
                              },
                            }
                          : {})}
                      />
                    ) : null}
                    <Button
                      type="primary"
                      onClick={() => {
                        setConfigureVisible(true)
                      }}
                      style={{ marginRight: 6 }}
                    >
                      {t('全局参数配置')}
                    </Button>
                    <Button
                      disabled={selectedUsCaseRowKeys.length === 0}
                      type="primary"
                      onClick={() => {
                        setAddTemplateModal({ visible: true, ids: selectedUsCaseRowKeys })
                      }}
                    >
                      {t('保存为模板')}
                    </Button>
                  </div>
                </div>
                <Checkbox
                  disabled={usCaseList.length === 0}
                  checked={isAllSelected}
                  indeterminate={selectedUsCaseRowKeys.length > 0 && !isAllSelected}
                  onChange={e => {
                    if (e.target.checked) {
                      handleSelectedUsCaseSelectAll(true)
                    } else {
                      handleSelectedUsCaseSelectAll(false)
                    }
                  }}
                >
                  {t('全选所有')}
                </Checkbox>
                <div className="table-wrapper">
                  <Scrollbars>
                    <Table
                      loading={getUsCaseListLoading}
                      columns={usCaseColumns}
                      dataSource={usCaseList}
                      bordered={false}
                      rowKey="resourceId"
                      scroll={{ x: 300 }}
                      onChange={handleUsCaseTableChange}
                      rowSelection={{
                        selectedRowKeys: selectedUsCaseRowKeys,
                        onSelect: (record, selected) =>
                          handleSelectedUsCaseRowKeys(record, selected),
                        onSelectAll: selected => handleSelectAllCurrentPageUsCase(selected),
                      }}
                      pagination={{
                        total: usCaseTotal,
                        showTotal: totalNum => (
                          <span>
                            {t('共')}
                            <span style={{ color: '#4238c9' }}>{totalNum}</span>
                            {t('条')}
                          </span>
                        ),

                        pageSize: usCaseSearchParams.pageSize,
                        current: usCaseSearchParams.page,
                        pageSizeOptions: ['10', '20', '30', '40', '50'],
                        showSizeChanger: true,
                        showQuickJumper: true,
                      }}
                    />
                  </Scrollbars>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="footer">
          {step === 1 && (
            <Button type="primary" onClick={() => form.submit()}>
              {t('下一步')}
            </Button>
          )}
          {step === 2 && (
            <>
              <Button type="primary" onClick={handleBack}>
                {t('上一步')}
              </Button>
              <Button loading={createTaskLoading} type="primary" onClick={handleCreateTask}>
                {t('创建任务')}
              </Button>
            </>
          )}
        </div>
      </div>
      <AddTemplateModal
        resourceIds={addTemplateModal.ids}
        visible={addTemplateModal.visible}
        onClose={() => setAddTemplateModal({ visible: false, ids: [] })}
      />

      <DetailDrawer
        id={detailDrawer.id}
        visible={detailDrawer.visible}
        onCancel={() => setDetailDrawer({ visible: false, id: '' })}
      />

      <Configure configureVisible={configureVisible} setConfigureVisible={setConfigureVisible} />
    </Wrapper>
  )
}

export default CreateTask

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px;

  .container {
    width: 100%;
    height: 100%;
    padding: 14px 0 0 0;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
    display: flex;
    flex-direction: column;
    .ant-form-item-label > label {
      font-weight: 500;
    }

    .header {
      display: flex;
      flex-direction: column;
      padding: 0 16px;

      .title {
        font-weight: 500;
        font-size: 16px;
      }

      .steps-container {
        width: 46.4%;
        height: 76px;
        margin: 16px auto;
        padding: 0 6.2%;
        background: rgba(66, 56, 201, 0.04);
        border: 1px dashed #afabe8;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        position: relative;

        .step-number {
          width: 28px;
          height: 28px;
          text-align: center;
          line-height: 28px;
          font-size: 20px;
          font-weight: 500;
          color: #86909c;
          background: rgba(255, 255, 255, 0.5);
          font-family: 'DIN Alternate', apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
            'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
          font-style: italic;

          &.active {
            color: #4238c9;
          }

          &:first-child {
            position: absolute;
            left: 12%;
            top: -18px;
          }

          &:nth-child(2) {
            position: absolute;
            left: 60%;
            top: -18px;
          }
        }

        .step-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;

          .step-icon {
            margin-right: 10px;
          }

          .step-text {
            margin-right: 60px;
            color: #999;
            flex-shrink: 0;

            &.active {
              color: #333;
            }

            &:last-child {
              margin-right: 0;
            }
          }
        }

        .step-arrow {
          margin: 0 30px;
        }
      }
    }

    .content {
      flex: 1;
      padding: 0 16px;
      overflow-y: auto;

      .section {
        margin-bottom: 24px;

        &:last-child {
          border-bottom: none;
        }

        .ant-form-item-label {
          width: 80px;
          flex-wrap: wrap;
        }

        .agent-to-host-agent-id-form-item,
        .agent-to-listener-agent-id-form-item {
          .ant-form-item-explain-error {
            margin-left: 16px;
          }
        }

        .agent-item {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .agent-item-name {
            margin-right: 12px;
          }
        }

        .custom-label {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .section-title {
          font-size: 14px;
          font-weight: 500;
          color: #1d2129;
          position: relative;

          &::after {
            content: '';
            display: block;
            position: absolute;
            left: 0;
            bottom: 0;
            width: 88px;
            height: 6px;
            background: linear-gradient(90deg, #2090a8 0%, #4238c9 100%);
            border-radius: 0px 86px 0px 100px;
            margin-top: 8px;
          }

          .section-bar {
            content: '';
            display: block;
            width: 100%;
            height: 4px;
            background: #ebeaf9;
            margin-top: 4px;
            margin-bottom: 20px;
          }
        }

        .form-content {
          margin-left: 34px;
        }

        .label-with-tooltip {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .radio-group {
          display: flex;
          align-items: center;

          .ant-radio-wrapper {
            margin-bottom: 0;
          }

          .ant-radio-wrapper span:last-child {
            line-height: 32px;
          }
        }

        .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          border-color: #d9d9d9;
          background-color: #e9e8f9;

          span {
            font-weight: 500;
          }

          &::before {
            content: none;
          }
        }

        .config-section {
          margin: 0px 0 12px 78px;
          padding: 16px;
          background: #f8f9fb;
          border-radius: 8px;
          font-weight: 400;
          .ant-form-item-label > label {
            font-weight: 400;
          }

          .config-title {
            font-size: 14px;
            color: #4e5969;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .config-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 16px;
          }
        }
      }

      .wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .template-wrapper {
          width: 44%;
          height: 636px;
          border-radius: 8px;
          border: 1px solid #d9d9d9;
          display: flex;
          flex-direction: column;
          padding: 14px 16px 0 16px;

          .title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 14px;

            span:first-child {
              font-size: 14px;
              font-weight: 500;
            }

            .ant-input-affix-wrapper {
              width: 260px;
            }
          }

          .table-wrapper {
            margin-top: 14px;
            flex: 1;
            overflow-y: auto;

            .ant-space {
              gap: 0 !important;

              &.space-btn .ant-space-item {
                margin-right: 8px !important;
              }
            }

            .ant-table-column-sorter {
              display: flex;
              justify-content: center;
              align-items: center;
            }

            .ant-table-column-sorters {
              justify-content: flex-start;

              .ant-table-column-title {
                flex: none;
              }
            }

            .ant-table-filter-column {
              justify-content: flex-start;

              .ant-table-column-title {
                flex: unset;
              }

              .ant-table-filter-trigger:hover {
                background: transparent;
              }
            }

            .ant-table-measure-row {
              display: none;
            }
          }
        }

        .case-wrapper {
          width: 54%;
          height: 636px;
          border-radius: 8px;
          border: 1px solid #d9d9d9;
          display: flex;
          flex-direction: column;
          padding: 14px 16px;

          .title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 14px;

            span:first-child {
              font-size: 14px;
              font-weight: 500;
            }

            .right {
              .ant-select:first-child {
                width: 100px;
                margin-right: -1px;

                .ant-select-selector {
                  border-radius: 6px 0 0 6px;
                }
              }

              .custom-cascader {
                width: 155px;
                margin-right: 8px;

                &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
                  border-radius: 0 6px 6px 0;
                }
              }

              .ant-input {
                width: 155px;
                border-radius: 0 6px 6px 0;
                margin-right: 8px;

                &::placeholder {
                  color: #c9cdd4;
                }
              }
            }
          }

          .table-wrapper {
            margin-top: 14px;
            flex: 1;
            overflow-y: auto;

            .ant-space {
              gap: 0 !important;

              &.space-btn .ant-space-item {
                margin-right: 8px !important;
              }
            }

            .ant-table-column-sorter {
              display: flex;
              justify-content: center;
              align-items: center;
            }

            .ant-table-column-sorters {
              justify-content: flex-start;

              .ant-table-column-title {
                flex: none;
              }
            }

            .ant-table-filter-column {
              justify-content: flex-start;

              .ant-table-column-title {
                flex: unset;
              }

              .ant-table-filter-trigger:hover {
                background: transparent;
              }
            }

            .ant-table-measure-row {
              display: none;
            }
          }
        }
      }
    }

    .footer {
      height: 56px;
      padding: 0 20px;
      border-top: 1px solid #d5d9dd;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
    }
  }

  .ant-form {
    .ant-form-item {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-radio-wrapper {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-radio-button-wrapper {
      height: 32px;
      padding: 0 16px;
      line-height: 30px;
    }
  }
`

const TypeTag = styled.span<{ bgColor: string; color: string }>`
  display: inline-block;
  height: 18px;
  padding: 0 4px;
  background: ${props => props.bgColor};
  border-radius: 4px;
  line-height: 18px;
  color: ${props => props.color};
  font-size: 12px;
  margin-right: 4px;
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
